import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { debounce, prefersReducedMotion } from '@theme/utilities';
import { setupModalDeeplink } from '@theme/modal-deeplink';

const ACTIVE_DEBOUNCE_MS = 100;
const PROGRAMMATIC_SCROLL_BUFFER_MS = 220;

class EndorsementModal extends DialogComponent {
  #panes = [];
  #railItems = [];
  #track = null;
  #liveRegion = null;
  #triggers = [];
  #lastTrigger = null;
  #activeIndex = 0;
  #observer = null;
  #isOpening = false;
  #isProgrammaticScroll = false;
  #progScrollTimer = 0;
  #liveRegionTimer = 0;
  #cleanupDeeplink = null;

  connectedCallback() {
    super.connectedCallback();

    this.#track = this.querySelector('[data-track]');
    this.#liveRegion = this.querySelector('[data-live-region]');
    this.#panes = /** @type {HTMLElement[]} */ ([...this.querySelectorAll('[data-endorsement-pane]')]);
    this.#railItems = /** @type {HTMLButtonElement[]} */ ([...this.querySelectorAll('[data-rail-item]')]);

    this.#bindTriggers();
    this.#bindRailItems();
    this.#createObserver();

    this.addEventListener(DialogCloseEvent.eventName, this.#onDialogClose);
    this.addEventListener('bottom-drawer:close', this.#handleDrawerClose);
    this.#cleanupDeeplink = setupModalDeeplink(this, {
      id: this.dataset.modalId,
      open: () => this.#openAt(0),
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#observer) this.#observer.disconnect();

    for (const trigger of this.#triggers) {
      trigger.removeEventListener('click', this.#handleTriggerClick);
    }
    this.#triggers = [];

    for (const item of this.#railItems) {
      item.removeEventListener('click', this.#handleRailClick);
      item.removeEventListener('keydown', this.#handleRailKeydown);
    }

    this.removeEventListener(DialogCloseEvent.eventName, this.#onDialogClose);
    this.removeEventListener('bottom-drawer:close', this.#handleDrawerClose);
    window.clearTimeout(this.#progScrollTimer);
    window.clearTimeout(this.#liveRegionTimer);
    this.#cleanupDeeplink?.();
  }

  closeModal = () => this.closeDialog();

  // ----- Wiring -----

  #bindTriggers() {
    const blockId = this.getAttribute('data-block-id');
    if (!blockId) return;
    const selector = `[data-endorsements-block="${CSS.escape(blockId)}"] [data-endorsement-trigger]`;
    this.#triggers = /** @type {HTMLElement[]} */ ([...document.querySelectorAll(selector)]);
    for (const trigger of this.#triggers) {
      trigger.addEventListener('click', this.#handleTriggerClick);
    }
  }

  #bindRailItems() {
    for (const item of this.#railItems) {
      item.addEventListener('click', this.#handleRailClick);
      item.addEventListener('keydown', this.#handleRailKeydown);
    }
  }

  #createObserver() {
    if (!this.#track || !this.#panes.length) return;
    // Vertical detection: a pane counts as active when its top edge is in
    // the upper-middle band of the body's viewport.
    this.#observer = new IntersectionObserver(this.#handleIntersect, {
      root: this.#track,
      rootMargin: '-25% 0px -65% 0px',
      threshold: 0,
    });
    for (const pane of this.#panes) this.#observer.observe(pane);
  }

  #handleIntersect = (entries) => {
    for (const entry of entries) {
      if (!entry.isIntersecting) continue;
      const index = Number(/** @type {HTMLElement} */ (entry.target).dataset.endorsementIndex);
      if (Number.isNaN(index)) continue;
      if (this.#isOpening) {
        this.#applyActive(index);
        this.#announceActive();
        this.#isOpening = false;
      } else {
        this.#debouncedSetActive(index);
      }
    }
  }

  #debouncedSetActive = debounce((index) => {
    this.#applyActive(index);
    if (!this.#isProgrammaticScroll) this.#announceActive();
  }, ACTIVE_DEBOUNCE_MS);

  // ----- Active state -----

  #applyActive(index) {
    if (index < 0 || index >= this.#panes.length) return;
    if (index === this.#activeIndex) return;
    this.#activeIndex = index;

    // Organic scroll only — tap/keyboard navigation publishes from
    // #scrollToPane, and intermediate panes passing through during a
    // programmatic scroll aren't real views.
    if (!this.#isProgrammaticScroll) {
      window.publishStorefrontEvent?.('endorsement_viewed', {
        index,
        total: this.#panes.length,
        method: 'scroll',
      });
    }

    for (const item of this.#railItems) {
      const itemIndex = Number(item.dataset.endorsementIndex);
      const isActive = itemIndex === index;
      item.setAttribute('aria-selected', isActive ? 'true' : 'false');
      item.tabIndex = isActive ? 0 : -1;
      item.classList.toggle('is-active', isActive);
    }

    // Don't yank the rail around as intermediate panes pass through
    // during a tap-driven scroll — let the destination land first.
    if (!this.#isProgrammaticScroll) this.#scrollActiveRailIntoView();
  }

  #scrollActiveRailIntoView() {
    const active = this.#railItems[this.#activeIndex];
    if (!active) return;
    const behavior = prefersReducedMotion() ? 'instant' : 'smooth';
    // Mobile rail is horizontal, desktop rail is vertical; scrollIntoView
    // with both axes set to nearest/center handles both layouts.
    active.scrollIntoView(/** @type {ScrollIntoViewOptions} */ ({
      inline: 'center',
      block: 'nearest',
      behavior,
    }));
  }

  // ----- Live region -----

  #announceActive() {
    if (!this.#liveRegion) return;
    const pane = this.#panes[this.#activeIndex];
    if (!pane) return;
    const name = pane.dataset.endorserName || '';
    const title = pane.dataset.endorserTitle || '';
    const total = this.#panes.length;
    const current = this.#activeIndex + 1;
    const parts = [`Endorsement ${current} of ${total}`];
    if (name) parts.push(`by ${name}`);
    if (title) parts.push(title);
    const message = parts.join(', ');

    // Clearing first ensures the same string re-announces if the user
    // navigates back to the same card.
    this.#liveRegion.textContent = '';
    window.clearTimeout(this.#liveRegionTimer);
    this.#liveRegionTimer = window.setTimeout(() => {
      if (this.#liveRegion) this.#liveRegion.textContent = message;
    }, 50);
  }

  // ----- Rail interaction -----

  #handleRailClick = (event) => {
    const item = /** @type {HTMLElement} */ (event.currentTarget);
    const index = Number(item.dataset.endorsementIndex);
    if (Number.isNaN(index)) return;
    this.#scrollToPane(index);
  };

  #handleRailKeydown = (event) => {
    let nextIndex = this.#activeIndex;
    switch (event.key) {
      case 'ArrowRight':
      case 'ArrowDown':
        nextIndex = Math.min(this.#railItems.length - 1, this.#activeIndex + 1);
        break;
      case 'ArrowLeft':
      case 'ArrowUp':
        nextIndex = Math.max(0, this.#activeIndex - 1);
        break;
      case 'Home':
        nextIndex = 0;
        break;
      case 'End':
        nextIndex = this.#railItems.length - 1;
        break;
      default:
        return;
    }
    if (nextIndex === this.#activeIndex) return;
    event.preventDefault();
    this.#scrollToPane(nextIndex, { focus: true });
  };

  #scrollToPane(index, { focus = false } = {}) {
    const pane = this.#panes[index];
    if (!pane || !this.#track) return;
    if (index !== this.#activeIndex) {
      window.publishStorefrontEvent?.('endorsement_viewed', {
        index,
        total: this.#panes.length,
        method: 'rail',
      });
    }
    const targetY = this.#paneScrollTop(pane);
    this.#isProgrammaticScroll = true;

    if (prefersReducedMotion()) {
      this.#track.scrollTop = targetY;
    } else {
      this.#track.scrollTo({ top: targetY, behavior: 'smooth' });
    }

    // The IO will update active as the destination intersects, but for
    // immediate feedback (and so #scrollActiveRailIntoView fires correctly
    // once the flag clears) update the index now.
    this.#applyActive(index);

    window.clearTimeout(this.#progScrollTimer);
    this.#progScrollTimer = window.setTimeout(() => {
      this.#isProgrammaticScroll = false;
      this.#scrollActiveRailIntoView();
      this.#announceActive();
    }, prefersReducedMotion() ? 0 : PROGRAMMATIC_SCROLL_BUFFER_MS);

    if (focus) {
      const target = this.#railItems[index];
      if (target) target.focus({ preventScroll: true });
    }
  }

  /**
   * Compute a pane's target scroll offset inside the track, regardless of
   * offsetParent.
   */
  #paneScrollTop(pane) {
    if (!this.#track || !pane) return 0;
    const containerTop = this.#track.getBoundingClientRect().top;
    const paneTop = pane.getBoundingClientRect().top;
    return this.#track.scrollTop + (paneTop - containerTop);
  }

  // ----- Open / close -----

  #handleTriggerClick = (event) => {
    event.preventDefault();
    const trigger = /** @type {HTMLElement} */ (event.currentTarget);
    this.#lastTrigger = trigger;
    const indexAttr = trigger.dataset.endorsementIndex;
    const targetIndex = indexAttr != null ? Number(indexAttr) : -1;
    this.#openAt(Number.isNaN(targetIndex) ? 0 : targetIndex);
  };

  #openAt(rawIndex) {
    this.#isOpening = true;
    this.showDialog();

    const open = () => {
      if (!this.#track) return;
      const index = rawIndex < 0 ? 0 : Math.min(rawIndex, this.#panes.length - 1);

      if (index === 0) {
        this.#track.scrollTo({ top: 0, behavior: 'instant' });
      } else {
        const pane = this.#panes[index];
        if (pane) {
          this.#track.scrollTo({ top: this.#paneScrollTop(pane), behavior: 'instant' });
        }
      }

      this.#applyActive(index);
      this.#announceActive();
      this.#isOpening = false;

      const focusTarget = this.#railItems[index]
        ?? this.querySelector('[data-close-trigger]')
        ?? this.#railItems[0];
      if (focusTarget) focusTarget.focus({ preventScroll: true });
    };

    // showDialog uses rAF internally before opening; defer so layout
    // is settled before we measure pane positions.
    requestAnimationFrame(() => requestAnimationFrame(open));
  }

  #handleDrawerClose = () => {
    this.closeDialog();
  };

  #onDialogClose = () => {
    if (this.#liveRegion) this.#liveRegion.textContent = '';
    if (this.#lastTrigger && typeof this.#lastTrigger.focus === 'function') {
      this.#lastTrigger.focus();
    }
  };
}

if (!customElements.get('endorsement-modal')) {
  customElements.define('endorsement-modal', EndorsementModal);
}
